/**
 * Validatori Fiscali per FatturaPA - Frontend
 * Validazione formato Codice Fiscale, Partita IVA, CAP, Provincia
 * secondo le specifiche dell'Agenzia delle Entrate
 */

// Mappa caratteri pari per calcolo check digit CF
const evenMap = {
  '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
  'A': 0, 'B': 1, 'C': 2, 'D': 3, 'E': 4, 'F': 5, 'G': 6, 'H': 7, 'I': 8, 'J': 9,
  'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18,
  'T': 19, 'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25
};

// Mappa caratteri dispari per calcolo check digit CF
const oddMap = {
  '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
  'A': 1, 'B': 0, 'C': 5, 'D': 7, 'E': 9, 'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21,
  'K': 2, 'L': 4, 'M': 18, 'N': 20, 'O': 11, 'P': 3, 'Q': 6, 'R': 8, 'S': 12,
  'T': 14, 'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23
};

const controlChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Calcola il carattere di controllo atteso per un Codice Fiscale
 * @param {string} cf - Primi 15 caratteri del CF
 * @returns {string} - Carattere di controllo atteso
 */
export const calculateCFCheckDigit = (cf) => {
  if (!cf || cf.length < 15) return '';
  
  const first15 = cf.toUpperCase().substring(0, 15);
  let total = 0;
  
  for (let i = 0; i < 15; i++) {
    const char = first15[i];
    if (i % 2 === 0) { // Posizioni dispari (indice pari)
      total += oddMap[char] || 0;
    } else { // Posizioni pari (indice dispari)
      total += evenMap[char] || 0;
    }
  }
  
  return controlChars[total % 26];
};

/**
 * Valida il Codice Fiscale italiano
 * @param {string} cf - Codice Fiscale da validare
 * @returns {{ isValid: boolean, error: string|null, expectedCheckDigit: string|null }}
 */
export const validateCodiceFiscale = (cf) => {
  if (!cf) return { isValid: true, error: null };
  
  cf = cf.toUpperCase().trim();
  
  // Lunghezza
  if (cf.length !== 16) {
    return { 
      isValid: false, 
      error: `Il Codice Fiscale deve essere di 16 caratteri (inseriti: ${cf.length})` 
    };
  }
  
  // Solo alfanumerico
  if (!/^[A-Z0-9]{16}$/.test(cf)) {
    return { 
      isValid: false, 
      error: 'Il Codice Fiscale può contenere solo lettere e numeri' 
    };
  }
  
  // Verifica check digit
  const expectedCheckDigit = calculateCFCheckDigit(cf);
  if (cf[15] !== expectedCheckDigit) {
    return { 
      isValid: false, 
      error: `Carattere di controllo errato (atteso: ${expectedCheckDigit})`,
      expectedCheckDigit
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Valida la Partita IVA italiana
 * @param {string} piva - Partita IVA da validare
 * @returns {{ isValid: boolean, error: string|null }}
 */
export const validatePartitaIVA = (piva) => {
  if (!piva) return { isValid: true, error: null };
  
  // Rimuovi prefisso IT se presente
  piva = piva.toUpperCase().trim().replace(/^IT/, '');
  
  // Lunghezza
  if (piva.length !== 11) {
    return { 
      isValid: false, 
      error: `La Partita IVA deve essere di 11 cifre (inserite: ${piva.length})` 
    };
  }
  
  // Solo numeri
  if (!/^\d{11}$/.test(piva)) {
    return { 
      isValid: false, 
      error: 'La Partita IVA può contenere solo cifre numeriche' 
    };
  }
  
  // Algoritmo Luhn modificato
  let total = 0;
  for (let i = 0; i < 11; i++) {
    const d = parseInt(piva[i], 10);
    if (i % 2 === 0) { // Posizione dispari (indice pari)
      total += d;
    } else { // Posizione pari (indice dispari)
      const doubled = d * 2;
      total += doubled > 9 ? doubled - 9 : doubled;
    }
  }
  
  if (total % 10 !== 0) {
    return { 
      isValid: false, 
      error: 'La Partita IVA non è valida (check digit errato)' 
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Valida il CAP italiano
 * @param {string} cap - CAP da validare
 * @returns {{ isValid: boolean, error: string|null }}
 */
export const validateCAP = (cap) => {
  if (!cap) return { isValid: true, error: null };
  
  cap = cap.trim();
  
  // Lunghezza
  if (cap.length !== 5) {
    return { 
      isValid: false, 
      error: `Il CAP deve essere di 5 cifre (inserite: ${cap.length})` 
    };
  }
  
  // Solo numeri
  if (!/^\d{5}$/.test(cap)) {
    return { 
      isValid: false, 
      error: 'Il CAP può contenere solo cifre numeriche' 
    };
  }
  
  // Range valido
  const capNum = parseInt(cap, 10);
  if (capNum < 10 || capNum > 98999) {
    return { 
      isValid: false, 
      error: 'Il CAP inserito non sembra essere valido' 
    };
  }
  
  return { isValid: true, error: null };
};

// Lista province italiane
const provinceValide = new Set([
  'AG', 'AL', 'AN', 'AO', 'AR', 'AP', 'AT', 'AV', 'BA', 'BT', 'BL', 'BN', 'BG',
  'BI', 'BO', 'BZ', 'BS', 'BR', 'CA', 'CL', 'CB', 'CI', 'CE', 'CT', 'CZ', 'CH',
  'CO', 'CS', 'CR', 'KR', 'CN', 'EN', 'FM', 'FE', 'FI', 'FG', 'FC', 'FR', 'GE',
  'GO', 'GR', 'IM', 'IS', 'SP', 'AQ', 'LT', 'LE', 'LC', 'LI', 'LO', 'LU', 'MC',
  'MN', 'MS', 'MT', 'VS', 'ME', 'MI', 'MO', 'MB', 'NA', 'NO', 'NU', 'OG', 'OT',
  'OR', 'PD', 'PA', 'PR', 'PV', 'PG', 'PU', 'PE', 'PC', 'PI', 'PT', 'PN', 'PZ',
  'PO', 'RG', 'RA', 'RC', 'RE', 'RI', 'RN', 'RM', 'RO', 'SA', 'SS', 'SV', 'SI',
  'SR', 'SO', 'SU', 'TA', 'TE', 'TR', 'TO', 'TP', 'TN', 'TV', 'TS', 'UD', 'VA',
  'VE', 'VB', 'VC', 'VR', 'VV', 'VI', 'VT'
]);

/**
 * Valida la sigla provincia italiana
 * @param {string} provincia - Sigla provincia da validare
 * @returns {{ isValid: boolean, error: string|null }}
 */
export const validateProvincia = (provincia) => {
  if (!provincia) return { isValid: true, error: null };
  
  provincia = provincia.toUpperCase().trim();
  
  // Lunghezza
  if (provincia.length !== 2) {
    return { 
      isValid: false, 
      error: `La Provincia deve essere di 2 lettere (inserite: ${provincia.length})` 
    };
  }
  
  // Solo lettere
  if (!/^[A-Z]{2}$/.test(provincia)) {
    return { 
      isValid: false, 
      error: 'La Provincia può contenere solo lettere' 
    };
  }
  
  // Provincia valida
  if (!provinceValide.has(provincia)) {
    return { 
      isValid: false, 
      error: `'${provincia}' non è una sigla provincia valida` 
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Valida il codice SDI/IPA
 * @param {string} sdiCode - Codice SDI/IPA da validare
 * @param {string} customerType - Tipo cliente (B2B o PA)
 * @returns {{ isValid: boolean, error: string|null }}
 */
export const validateSDICode = (sdiCode, customerType = 'B2B') => {
  if (!sdiCode) return { isValid: true, error: null };
  
  sdiCode = sdiCode.toUpperCase().trim();
  
  if (customerType === 'PA') {
    // Codice IPA: 6 caratteri alfanumerici
    if (sdiCode.length !== 6) {
      return { 
        isValid: false, 
        error: `Il Codice IPA per la PA deve essere di 6 caratteri (inseriti: ${sdiCode.length})` 
      };
    }
    if (!/^[A-Z0-9]{6}$/.test(sdiCode)) {
      return { 
        isValid: false, 
        error: 'Il Codice IPA può contenere solo lettere e numeri' 
      };
    }
  } else {
    // Codice Destinatario: 7 caratteri alfanumerici
    if (sdiCode.length !== 7 && sdiCode !== '0000000') {
      return { 
        isValid: false, 
        error: `Il Codice Destinatario deve essere di 7 caratteri (inseriti: ${sdiCode.length})` 
      };
    }
    if (!/^[A-Z0-9]{7}$/.test(sdiCode)) {
      return { 
        isValid: false, 
        error: 'Il Codice Destinatario può contenere solo lettere e numeri' 
      };
    }
  }
  
  return { isValid: true, error: null };
};

/**
 * Valida il CIG
 * @param {string} cig - CIG da validare
 * @returns {{ isValid: boolean, error: string|null }}
 */
export const validateCIG = (cig) => {
  if (!cig) return { isValid: true, error: null };
  
  cig = cig.toUpperCase().trim();
  
  if (cig.length !== 10) {
    return { 
      isValid: false, 
      error: `Il CIG deve essere di 10 caratteri (inseriti: ${cig.length})` 
    };
  }
  
  if (!/^[A-Z0-9]{10}$/.test(cig)) {
    return { 
      isValid: false, 
      error: 'Il CIG può contenere solo lettere e numeri' 
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Valida il CUP
 * @param {string} cup - CUP da validare
 * @returns {{ isValid: boolean, error: string|null }}
 */
export const validateCUP = (cup) => {
  if (!cup) return { isValid: true, error: null };
  
  cup = cup.toUpperCase().trim();
  
  if (cup.length !== 15) {
    return { 
      isValid: false, 
      error: `Il CUP deve essere di 15 caratteri (inseriti: ${cup.length})` 
    };
  }
  
  if (!/^[A-Z0-9]{15}$/.test(cup)) {
    return { 
      isValid: false, 
      error: 'Il CUP può contenere solo lettere e numeri' 
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Valida indirizzo PEC
 * @param {string} pec - PEC da validare
 * @returns {{ isValid: boolean, error: string|null }}
 */
export const validatePEC = (pec) => {
  if (!pec) return { isValid: true, error: null };
  
  pec = pec.toLowerCase().trim();
  
  const emailPattern = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
  if (!emailPattern.test(pec)) {
    return { 
      isValid: false, 
      error: 'L\'indirizzo PEC non è in un formato valido' 
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Valida tutti i dati cliente in base al tipo
 * @param {Object} formData - Dati del form
 * @returns {{ isValid: boolean, errors: Object }}
 */
export const validateCustomerForm = (formData) => {
  const errors = {};
  
  // Validazione CAP (sempre)
  if (formData.zip_code) {
    const capValidation = validateCAP(formData.zip_code);
    if (!capValidation.isValid) errors.zip_code = capValidation.error;
  }
  
  // Validazione Provincia (sempre)
  if (formData.province) {
    const provValidation = validateProvincia(formData.province);
    if (!provValidation.isValid) errors.province = provValidation.error;
  }
  
  // Validazione specifica per tipo cliente
  if (formData.customer_type === 'B2C') {
    // B2C: CF obbligatorio
    if (!formData.fiscal_code) {
      errors.fiscal_code = 'Codice Fiscale obbligatorio per cliente privato';
    } else {
      const cfValidation = validateCodiceFiscale(formData.fiscal_code);
      if (!cfValidation.isValid) errors.fiscal_code = cfValidation.error;
    }
  } else if (formData.customer_type === 'B2B') {
    // B2B: P.IVA obbligatoria
    if (!formData.vat_number) {
      errors.vat_number = 'Partita IVA obbligatoria per azienda';
    } else {
      const pivaValidation = validatePartitaIVA(formData.vat_number);
      if (!pivaValidation.isValid) errors.vat_number = pivaValidation.error;
    }
    
    // SDI o PEC
    if (!formData.sdi_code && !formData.pec) {
      errors.sdi_code = 'Codice SDI o PEC obbligatorio';
    } else if (formData.sdi_code && formData.sdi_code !== '0000000') {
      const sdiValidation = validateSDICode(formData.sdi_code, 'B2B');
      if (!sdiValidation.isValid) errors.sdi_code = sdiValidation.error;
    }
    
    // PEC se SDI vuoto o 0000000
    if ((!formData.sdi_code || formData.sdi_code === '0000000') && formData.pec) {
      const pecValidation = validatePEC(formData.pec);
      if (!pecValidation.isValid) errors.pec = pecValidation.error;
    }
  } else if (formData.customer_type === 'PA') {
    // PA: P.IVA, IPA, CIG obbligatori
    if (!formData.vat_number) {
      errors.vat_number = 'Partita IVA obbligatoria per PA';
    } else {
      const pivaValidation = validatePartitaIVA(formData.vat_number);
      if (!pivaValidation.isValid) errors.vat_number = pivaValidation.error;
    }
    
    if (!formData.sdi_code) {
      errors.sdi_code = 'Codice IPA obbligatorio per PA';
    } else {
      const sdiValidation = validateSDICode(formData.sdi_code, 'PA');
      if (!sdiValidation.isValid) errors.sdi_code = sdiValidation.error;
    }
    
    if (!formData.cig) {
      errors.cig = 'CIG obbligatorio per PA';
    } else {
      const cigValidation = validateCIG(formData.cig);
      if (!cigValidation.isValid) errors.cig = cigValidation.error;
    }
    
    // CUP opzionale ma se presente va validato
    if (formData.cup) {
      const cupValidation = validateCUP(formData.cup);
      if (!cupValidation.isValid) errors.cup = cupValidation.error;
    }
  }
  
  // Validazione CF (sempre se presente)
  if (formData.fiscal_code && formData.customer_type !== 'B2C') {
    const cfValidation = validateCodiceFiscale(formData.fiscal_code);
    if (!cfValidation.isValid) errors.fiscal_code = cfValidation.error;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
